export type LaptopCore = {
  id: string;
  name: string;
  full_model_name: string;
  config_number: number;
  company: string;
  slug: string;
  series: string;
  form_factor: number;
  is_business_grade: boolean;
  release_year: string;
  // Null for ~93% of the catalog: most configs have no Amazon listing of their
  // own, and their price has to come from the sellers instead.
  amazon_price_pounds: number | null;
  amazon_product_id: string | null;
  img_urls: string[];
};

export type CPU = {
  name: string;
  // From laptopmedia_cpu
  cinebench_2024: number | null;
  review_single_core_performance: number | null;
  review_multi_core_performance: number | null;
  review_power_efficiency: number | null;
  review_integrated_graphics: number | null;
  review_final_score: number | null;
};

export type GPU = {
  name: string;
  // From laptopmedia_gpu
  three_d_mark_time_spy_g: number | null;
  review_gaming: number | null;
  review_ai_ml: number | null;
  review_energy_efficiency: number | null;
  review_final_score: number | null;
};

export type LaptopSpecs = {
  display: {
    inches: number;
    resolution: string;
    panel_type: string | null;
    // Tri-state, not a plain boolean: null means we have no evidence either way,
    // which is the common case since LaptopMedia publishes no touchscreen field.
    is_touchscreen: boolean | null;
  };
  storage: {
    size_gb: number;
    type: string;
  };
  ram: {
    size_gb: number;
    generation: string | null;
    speed_mhz: number | null;
  };
  weight_kg: number;
  dimensions: {
    length_mm: number;
    width_mm: number;
    height_mm: number;
  };
  operating_system: string;
  body_material: string | null;
};

export type Connectivity = {
  num_usb_a: number;
  usb_a_details: [number, string][];
  num_usb_c: number;
  usb_c_details: [number, string][];
  wifi_version: string;
  bluetooth_version: string;
  has_hdmi: boolean;
  hdmi_details: string | null;
  has_ethernet: boolean;
  ethernet_details: string | null;
  has_audio_jack: boolean;
  audio_jack_details: string | null;
  has_mini_display_port: boolean;
  mini_display_port_details: string | null;
  has_card_reader: boolean;
  card_reader_details: string | null;
};

export type LaptopFeatures = {
  has_finger_print_reader: boolean;
  finger_print_reader_details: string | null;
  has_web_camera: boolean;
  web_camera_details: string | null;
  has_backlit_keyboard: boolean;
  backlit_keyboard_details: string | null;
  has_microphone: boolean;
  microphone_details: string | null;
  has_speakers: boolean;
  speakers_details: string | null;
  optical_drive_details: string | null;
  security_lock_slot_details: string | null;
  m_two_slot_details: string | null;
};

export type SellerCondition = "new" | "refurbished";

// Back Market's Fair/Good/Excellent/Premium and eBay's condition IDs both map onto
// this one ladder so the two can be compared and sorted together. Ordered best to
// worst by CONDITION_GRADE_ORDER below.
export type ConditionGrade = "pristine" | "excellent" | "good" | "fair" | "unspecified";

export const CONDITION_GRADE_ORDER: ConditionGrade[] = [
  "pristine",
  "excellent",
  "good",
  "fair",
  "unspecified",
];

// What each normalised grade means, for the tooltip on a condition badge.
export const CONDITION_GRADE_BLURB: Record<ConditionGrade, string> = {
  pristine: "As new — professionally refurbished with no cosmetic marks",
  excellent: "Very light marks, not visible at arm's length",
  good: "Visible but minor cosmetic marks",
  fair: "Clearly used, with cosmetic wear",
  unspecified: "Refurbished, but the seller did not state a grade",
};

export type Seller = {
  id: string;
  seller_name: string;
  vendor_name: string;
  amazon_asin: string;
  title: string;
  item_price: number | null;
  total_price: number | null;
  delivery_price: number | null;
  currency: string;
  rating: number;
  reviews_count: number;
  seller_url: string;
  images: string[];
  // Per-seller spec details (optional — present from the recommendation API)
  in_stock?: boolean | null;
  operating_system?: string | null;
  processor?: string | null;
  memory_size?: number | null;
  memory_generation?: string | null;
  memory_speed_in_mhz?: number | null;
  storage_size_in_gigabtyes?: number | null;
  storage_type?: string | null;
  display_inches?: number | null;
  display_resolution?: string | null;
  display_panel_type?: string | null;
  graphics?: string | null;
  // Condition. Absent or null means new — only listings from the refurbished
  // sellers (eBay, Back Market) carry "refurbished".
  condition?: SellerCondition | null;
  condition_grade?: ConditionGrade | null;
  condition_label?: string | null;
  condition_description?: string | null;
  // Whether each spec exactly matches this laptop's configuration
  operating_system_match?: boolean;
  ram_size_match?: boolean;
  storage_size_match?: boolean;
  display_size_match?: boolean;
  graphics_match?: boolean;
  processor_match?: boolean;
  spec_match_count?: number;
};

/** One observed price. `d` is a bare YYYY-MM-DD date, `p` the item price. */
export type PricePoint = {
  d: string;
  p: number;
};

export type SellerPriceHistory = {
  id: string;
  seller_name: string | null;
  condition: string;
  points: PricePoint[];
};

/**
 * Price history for one laptop config, from
 * GET /recommend/laptop/{country}/{series}/{config}/price-history.
 *
 * `lowest_new` is the cheapest new listing at each capture date, which is what
 * the page charts — following a single seller would end the line whenever that
 * listing stopped being scraped. `sellers` carries each listing's own points for
 * the sparklines in the buy drawer, refurbished ones included.
 */
export type PriceHistoryResponse = {
  currency: string | null;
  range: string;
  first_captured_at: string | null;
  lowest_new: PricePoint[];
  sellers: SellerPriceHistory[];
};

export type AmazonProductDesc = {
  id: string;
  asin: string;
  title: string;
  description: string;
  rating: number;
  reviews_count: number;
  initial_price: number;
  final_price: number;
  currency: string;
  image_url: string;
  images: string[];
  customer_says: string | null;
  top_review: string | null;
  is_available: boolean;
  amazon_choice: boolean;
  amazon_prime: boolean;
};

export type UserReview = {
  id: string;
  asin: string;
  rating: number;
  reviewer_name: string;
  title: string;
  review_text: string;
  review_posted_date: string;
  review_country: string;
  helpful_count: number;
  variant_name: string | null;
};

export type WikiCitation = {
  url: string;
  title: string;
  author: string;
  icon_link: string;
  source_id: string;
  source_type: string;
  article_source: string;
};

export type Wiki = {
  page: {
    series_name: string;
    short_summary: string;
    deep_review: string;
    pros: string[];
    cons: string[];
    citations: {
      all: WikiCitation[];
      by_section: {
        cons: WikiCitation[];
        pros: WikiCitation[];
        deep_review: WikiCitation[];
        short_summary: WikiCitation[];
      };
    };
  };
};

export type LaptopScores = {
  overall: number;
  within_budget: boolean;
  performance: number | null;
  battery_life: number | null;
  storage_and_ram: number | null;
  form_factor: boolean;
  future_proof: number | null;
  value_for_money: number | null;
};

export type LaptopInformation = {
  rank: number;
  match_score: number;
  scores: LaptopScores;
  laptop: LaptopCore;
  cpu: CPU;
  gpu: GPU;
  specs: LaptopSpecs;
  connectivity: Connectivity;
  features: LaptopFeatures;
  sellers: Seller[];
  amazon_product_desc: AmazonProductDesc[];
  amazon_review_metadata: unknown[];
  user_reviews: UserReview[];
  wiki: Wiki;
};

export type IdealCpu = {
  name: string;
  cinebench_2024: number | null;
  review_single_core_performance: number | null;
  review_multi_core_performance: number | null;
  review_power_efficiency: number | null;
};

export type IdealGpu = {
  name: string;
  three_d_mark_time_spy_g: number | null;
  review_gaming: number | null;
  review_ai_ml: number | null;
  review_energy_efficiency: number | null;
};

export type RecommendationsResponse = {
  searched_through: number;
  total: number;
  laptops: LaptopInformation[];
  ideal_cpu: IdealCpu | null;
  ideal_gpu: IdealGpu | null;
};
